import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/5d96ae127f122e340202493ff75cd939ea9d1aaf56d7d13eb16d26279e7b7519.png",
    eyebrow: "The Sleep Collection",
    title: "Softness, woven by nature.",
    subtitle: "Premium bedding crafted for Pakistani homes — comforters, duvets, and the bedsheets you'll never want to leave.",
    cta: "Shop Bedding",
    href: "/category/bedding",
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/441cb965fc168cde911cb24ee4aade2aa209f656e8042730963495897acc41fb.png",
    eyebrow: "Winter Warmth",
    title: "Wrap up in quiet luxury.",
    subtitle: "Hand-finished blankets that hold heat, hold a hug, and hold their shape — season after season.",
    cta: "Shop Blankets",
    href: "/category/blankets",
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/9789fdc267bb5924073b16d729ad3de095efc77b79ce1c2eff9b0c6d2308858c.png",
    eyebrow: "Curtains & Home",
    title: "Let the light in, gently.",
    subtitle: "Standard 7.5 × 4.5 ft curtains and complementing towels — the soft edges of a home well lived.",
    cta: "Shop Curtains",
    href: "/category/curtains",
  },
];

const SWIPE_THRESHOLD = 45;

export default function HeroCarousel() {
  const [i, setI] = useState(0);
  const touchX = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  const next = () => setI((x) => (x + 1) % SLIDES.length);
  const prev = () => setI((x) => (x - 1 + SLIDES.length) % SLIDES.length);

  const onTouchStart = (e) => { touchX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx <= -SWIPE_THRESHOLD) next();
    else if (dx >= SWIPE_THRESHOLD) prev();
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-cream-warm"
      data-testid="hero-carousel"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[62vh] min-h-[420px] sm:h-[78vh] sm:min-h-[560px] max-h-[820px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img
              src={SLIDES[i].image}
              alt={SLIDES[i].title}
              className="w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              decoding="async"
            />
            {/* Stronger scrim on small screens so the headline stays readable over busy photos */}
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/35 to-ink/10 sm:from-ink/55 sm:via-ink/15 sm:to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex items-end pb-20 sm:pb-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={`text-${i}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="max-w-2xl text-cream"
              data-testid={`hero-slide-${i}`}
            >
              <div className="label-eyebrow !text-cream/80 !text-[10px] sm:!text-xs mb-3 sm:mb-4">{SLIDES[i].eyebrow}</div>
              <h1 className="font-display text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl text-cream sm:leading-[1.05] tracking-tight">
                {SLIDES[i].title}
              </h1>
              <p className="mt-3 sm:mt-6 text-cream/85 text-sm sm:text-lg max-w-lg leading-relaxed">{SLIDES[i].subtitle}</p>
              <Link
                to={SLIDES[i].href}
                className="inline-block mt-5 sm:mt-8 bg-cream text-ink hover:bg-sage hover:text-cream transition-colors duration-300 px-7 sm:px-8 py-4 text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold"
                data-testid={`hero-cta-${i}`}
              >
                {SLIDES[i].cta}
              </Link>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls — pointer devices only; mobile swipes instead */}
        <button
          onClick={prev}
          className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 bg-cream/20 hover:bg-cream/40 backdrop-blur p-3 rounded-full text-cream transition-colors"
          data-testid="hero-prev"
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 bg-cream/20 hover:bg-cream/40 backdrop-blur p-3 rounded-full text-cream transition-colors"
          data-testid="hero-next"
          aria-label="Next slide"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dots — the bar is small, the tap area is not */}
        <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center z-20">
          {SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              className="px-2 py-4 tap-target flex items-center justify-center"
              data-testid={`hero-dot-${idx}`}
              aria-label={`Slide ${idx + 1}`}
              aria-current={idx === i}
            >
              <span
                className={`block h-1.5 rounded-full transition-all ${idx === i ? "w-10 bg-cream" : "w-4 bg-cream/50"}`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
